import { Box, makeStyles } from '@material-ui/core';
import React, { useEffect } from 'react';
import { FunctionField, List, Record, ReduxState, ReferenceField, useLoading, useTranslate } from 'react-admin';
import { useDispatch, useSelector } from 'react-redux';
import { thresholds } from '../../pages/paths';
import { METADATA_AVATAR_TYPES, TRACEABILITY_NOTES_THRESHOLD_EXECUTIONS } from '../../providers/resources';
import { selectThresholds } from '../../redux/actions/thresholds';
import { CustomReducerState } from '../../redux/reducers';
import { tableStyles } from '../../theme/customTheme';
import { getNameTranslated } from '../../utils/threshold/setPropertyByLevel';
import { getNameWithoutPrefix } from '../../utils/resources/MetadataAvatarType';
import { MetadataAvatarType, TraceabilityNotesThresholdExecutions } from '../../utils/types';
import Chip from '@material-ui/core/Chip';
import DataGridCustom from '../UI/DataGrid';
import ActionList from './actions/managementThresholds/index';
import '../../styles/list.css';
import { PAGE_MAX_WIDTH } from '../../theme/crmColors';

const styles = () => ({
    ...tableStyles(),
    pageContainer: {
        display: 'grid',
        gap: 16,
        padding: 24,
        width: '100%',
        maxWidth: PAGE_MAX_WIDTH,
        margin: '0 auto',
        boxSizing: 'border-box',
        gridTemplateColumns: 'minmax(0, 1fr)',
        gridTemplateRows: 'auto auto 1fr',
        height: '100%',
        minHeight: '100%',
        '@media (max-width:600px)': {
            gap: 8,
            padding: 8,
        },
    },
    chip: {
        marginRight: 3,
    },
});
//@ts-ignore
const useStyles = makeStyles(styles);

const ThresholdsNotificationList = () => {
    const t = useTranslate();
    const classes = useStyles();
    const dispatch = useDispatch();
    const loading = useLoading();
    const selectedThreshold: TraceabilityNotesThresholdExecutions | Record | null = useSelector(
        (state: ReduxState & CustomReducerState) => {
            const selected = state.rikutec.thresholds.selected;
            const selectedThresholdUri = selected ? (selected as Record)['@id'] : null;
            const resourceData = state.admin.resources[TRACEABILITY_NOTES_THRESHOLD_EXECUTIONS]?.data;
            return selectedThresholdUri && resourceData && selectedThresholdUri in resourceData
                ? resourceData[selectedThresholdUri]
                : null;
        },
    );

    const listProps = {
        resource: TRACEABILITY_NOTES_THRESHOLD_EXECUTIONS,
        hasCreate: false,
        hasEdit: false,
        hasList: true,
        hasShow: false,
        basePath: thresholds,
        sort: { field: 'createdAt', order: 'ASC' },
        perPage: 10,
        filter: {},
    };

    // noinspection JSUnusedGlobalSymbols
    const dataGridProps = {
        rowClick: (id: string, basePath: string, record: Record) => {
            dispatch(selectThresholds.fn(record));
            return null;
        },
        toFindSelected: {
            resource: 'thresholds',
            key: 'selected',
        },
        hasBulkActions: null,
        classes: {
            checkbox: classes.checkbox,
            row: classes.list,
        },
    };

    useEffect(() => {
        if (selectedThreshold && selectedThreshold.hasOwnProperty('@id') && !loading) {
            dispatch(selectThresholds.fn((selectedThreshold as Record)['@id']));
        }
    }, [loading, dispatch, selectedThreshold]);

    return (
        <Box className={classes.pageContainer}>
            <Box className='list'>
                <List component='div' {...listProps} actions={<ActionList />} bulkActionButtons={false}>
                    <DataGridCustom {...dataGridProps}>
                        <ReferenceField
                            link={false}
                            sortable={true}
                            label={t('app.ancs.productTypology')}
                            reference={METADATA_AVATAR_TYPES}
                            source='metadataAvatarType'
                            sortBy='metadataAvatarType.name'
                        >
                            <FunctionField
                                sortable={true}
                                // @ts-ignore
                                render={(record: MetadataAvatarType) => `${getNameWithoutPrefix(record.name)}`}
                            />
                        </ReferenceField>

                        <FunctionField
                            label={t('app.traceabilityNotesThreshold.operationType')}
                            sortable
                            sortBy='appliedOnNoteAtTag'
                            // @ts-ignore
                            render={(record: TraceabilityNotesThresholdExecutions) => {
                                return t(`app.ancs.${record.appliedOnNoteAtTag.toLowerCase()}`);
                            }}
                        />

                        <FunctionField
                            label={t('app.traceabilityNotesThreshold.activeEvent')}
                            sortable={false}
                            // @ts-ignore
                            render={(record: TraceabilityNotesThresholdExecutions) => {
                                return record.appliedOnAtPropertyCreatedAtMetadata.map((item, index) => {
                                    return (
                                        <Chip
                                            className={classes.chip}
                                            label={t(getNameTranslated(item.name))}
                                            key={`function-field-${index}`}
                                        />
                                    );
                                });
                            }}
                        />
                    </DataGridCustom>
                </List>
            </Box>
        </Box>
    );
};

export default ThresholdsNotificationList;
